import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  colsForContainerWidth,
  packMasonryColumns,
  type MasonryPackItem,
} from "./templateMasonry";

type TemplateMasonryGridProps<T extends MasonryPackItem & { key: string }> = {
  items: T[];
  /** Soft ceiling from page cfg (`columns`). */
  maxCols?: number;
  /** Preview device — mobile forces 2 cols; tablet caps at 3. */
  device?: "desktop" | "tablet" | "mobile" | string;
  /** Fewer columns for text-only feeds (X / LinkedIn). */
  textOnly?: boolean;
  className?: string;
  style?: React.CSSProperties;
  gridRef?: React.Ref<HTMLDivElement>;
  /** Place in list order, one row at a time, instead of shortest-column. */
  fillRows?: boolean;
  /** One skeleton row in the next list slots while the next page loads. */
  pending?: boolean;
  /**
   * When false, cells that mount on this paint skip the rise. Already-mounted
   * cells keep whatever they decided on their own first paint, so a later
   * true does not replay the intro after a category change.
   */
  cellRise?: boolean;
  renderItem: (item: T, index: number) => React.ReactNode;
};

/**
 * The catalog unmounts while its skeleton is up. The next data paint is a new
 * grid, but riseHold is often still true on that paint, so category,
 * subcategory, shop, shoppable, and platform swaps popped in with no stagger.
 * Home tiles use that stagger. Arm it for the loader handoff only — scroll
 * pages mount after the flag clears.
 */
export function useRiseAfterCatalogLoader(
  showingLoader: boolean,
  hasItems: boolean,
) {
  const handoffRef = useRef(false);
  if (showingLoader) handoffRef.current = true;
  const riseAfterLoader = !showingLoader && hasItems && handoffRef.current;
  useLayoutEffect(() => {
    if (!riseAfterLoader) return;
    handoffRef.current = false;
  }, [riseAfterLoader]);
  return riseAfterLoader;
}

function MasonryRiseCell({
  index,
  rise,
  children,
}: {
  index: number;
  rise: boolean;
  children: React.ReactNode;
}) {
  const riseOnMount = React.useRef(rise);
  return (
    <div
      className="mte-tpl-masonry-cell"
      data-rise={riseOnMount.current ? "1" : undefined}
      data-tpl-masonry-index={index}
      style={
        {
          ["--mte-tile-stagger" as string]: String(index % 8),
        } as React.CSSProperties
      }
    >
      {children}
    </div>
  );
}

function resolveColCount(
  width: number,
  maxCols: number,
  device: string | undefined,
  textOnly: boolean,
): number {
  let cols = colsForContainerWidth(width);
  if (device === "mobile") cols = 2;
  else if (device === "tablet") cols = Math.min(cols, 3);
  cols = Math.min(cols, Math.max(1, maxCols));
  if (textOnly) cols = Math.min(cols, device === "mobile" ? 1 : 3);
  return Math.max(1, cols);
}

/**
 * Zig-zag masonry grid: flex columns.
 * Preserves original item index via `data-tpl-masonry-index` for infinite-scroll anchors.
 */
export function TemplateMasonryGrid<T extends MasonryPackItem & { key: string }>({
  items,
  maxCols = 6,
  device,
  textOnly = false,
  className = "",
  style,
  gridRef,
  fillRows = false,
  pending = false,
  cellRise = true,
  renderItem,
}: TemplateMasonryGridProps<T>) {
  const [width, setWidth] = useState(1200);
  const localRef = React.useRef<HTMLDivElement | null>(null);

  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      localRef.current = node;
      if (typeof gridRef === "function") gridRef(node);
      else if (gridRef && "current" in gridRef) {
        (gridRef as React.MutableRefObject<HTMLDivElement | null>).current =
          node;
      }
    },
    [gridRef],
  );

  // Before paint, so the first visible column count matches the container
  // (a 1200px guess is the 5-col breakpoint and then snaps to 6).
  useLayoutEffect(() => {
    const el = localRef.current;
    if (!el) return;
    let raf = 0;
    let lastW = 0;
    const WIDTH_EPS = 12;
    const measure = (nextW?: number) => {
      const w = nextW ?? el.getBoundingClientRect().width;
      if (!(w > 0)) return;
      // Ignore sub-column jitter during continuous window resize.
      if (lastW > 0 && Math.abs(w - lastW) < WIDTH_EPS) return;
      lastW = w;
      setWidth(w);
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      const onResize = () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => measure());
      };
      window.addEventListener("resize", onResize, { passive: true });
      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener("resize", onResize);
      };
    }
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => measure(w));
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  const cols = resolveColCount(width, maxCols, device, textOnly);

  const columns = useMemo(
    () => packMasonryColumns(items, cols, fillRows),
    [items, cols, fillRows],
  );

  const skeletonCols = useMemo(() => {
    const slots: number[][] = Array.from({ length: cols }, () => []);
    if (!pending) return slots;
    // One skeleton at the bottom of every column, same as the product loader.
    for (let i = 0; i < cols; i += 1) slots[i]!.push(i);
    return slots;
  }, [pending, cols]);

  // Map packed cells back to original flat index for stagger / analytics.
  const indexByKey = useMemo(() => {
    const map = new Map<string, number>();
    items.forEach((item, i) => map.set(item.key, i));
    return map;
  }, [items]);

  return (
    <div
      ref={setRefs}
      className={`mte-tpl-masonry${className ? ` ${className}` : ""}`}
      style={
        {
          ...style,
          ["--tpl-masonry-cols" as string]: String(cols),
        } as React.CSSProperties
      }
      data-tpl-masonry-cols={cols}
      aria-busy={pending || undefined}
    >
      {Array.from({ length: cols }, (_, ci) => {
        const col = columns[ci] ?? [];
        const skeletons = skeletonCols[ci] ?? [];
        if (!col.length && !skeletons.length) return null;
        return (
          <div key={ci} className="mte-tpl-masonry-col">
            {col.map((item) => {
              const index = indexByKey.get(item.key) ?? 0;
              return (
                <MasonryRiseCell key={item.key} index={index} rise={cellRise}>
                  {renderItem(item, index)}
                </MasonryRiseCell>
              );
            })}
            {skeletons.map((slot) => (
              <div key={`sk-${slot}`} className="mte-tpl-masonry-cell" aria-hidden>
                <div className="mte-tpl-masonry-skeleton" />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
